import React, { useCallback, useEffect, useState } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Spotlight, SpotlightTransition, Modal, ModalTransition } from '@atlaskit/onboarding';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { usePageLayoutResize } from '@atlassian/jira-navigation-system';
import { FireUiAnalytics, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import image from '../../assets/changeboarding.svg';
import { Flag } from '../../common/ui/flag';
import { useChangeboarding } from '../../controllers';
import { Spotlights } from '../../controllers/spotlights';
import { messages } from './messages';
import { useStep } from './utils';

export const Changeboarding = () => {
	const [{ visible }, { initChangeboarding, closeChangeboarding }] = useChangeboarding();
	const { formatMessage } = useIntl();
	const [step, { next, start, finish }] = useStep();

	useEffect(() => {
		initChangeboarding();
	}, [initChangeboarding]);

	useEffect(() => {
		if (visible) {
			start();
		}
	}, [start, visible]);

	const { expandLeftSidebar: expandLeftSidebarChangingEveryRender } = usePageLayoutResize();
	const [expandLeftSidebar] = useState(() => expandLeftSidebarChangingEveryRender);

	const openCommunityPost = useCallback(
		(_e: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'openedNewNavigationCommunityPost');
			next();
		},
		[next],
	);

	const startChangeboarding = useCallback(
		(_e: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'startedNewNavigationChangeboarding');
			next();
		},
		[next],
	);

	const skipChangeboarding = useCallback(
		(_e: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'skippedNewNavigationChangeboarding');
			finish();
		},
		[finish],
	);

	useEffect(() => {
		if (step === 'spotlight') {
			expandLeftSidebar();
		}
		if (step === 'flag') {
			closeChangeboarding({ silent: true });
		}
		if (step === 'finished') {
			closeChangeboarding();
		}
	}, [step, closeChangeboarding, expandLeftSidebar]);

	return (
		<>
			<ModalTransition>
				{visible && step === 'modal' && (
					<Modal
						heading={formatMessage(messages.modalHeading)}
						image={image}
						actions={[
							{
								onClick: startChangeboarding,
								text: formatMessage(messages.modalShowMe),
							},
							{
								onClick: skipChangeboarding,
								text: formatMessage(messages.modalExploreOnMyOwn),
							},
						]}
					>
						<FireUiAnalytics
							actionSubject="modal"
							action="viewed"
							actionSubjectId="newNavigationChangeboarding"
						/>
						<p>
							<FormattedMessage {...messages.modalContentParagraph1} />
						</p>
					</Modal>
				)}
			</ModalTransition>
			<SpotlightTransition>
				{visible && step === 'spotlight' && (
					<Spotlight
						actions={[
							{
								text: formatMessage(messages.viewsSpotlightNext),
								onClick: next,
							},
						]}
						heading={formatMessage(messages.viewsSpotlightTitle)}
						target={Spotlights.Views}
						key={Spotlights.Views}
						targetRadius={0}
						targetBgColor={token('elevation.surface', colors.N0)}
					>
						{formatMessage(messages.viewsSpotlightMessage)}
					</Spotlight>
				)}
			</SpotlightTransition>
			{visible && step === 'flag' && (
				<>
					<FireUiAnalytics
						actionSubject="flag"
						action="viewed"
						actionSubjectId="finishedNewNavigationChangeboarding"
					/>
					<Flag
						onDismissed={next}
						title={<FormattedMessage {...messages.flagTitle} />}
						description={<FormattedMessage {...messages.flagContent} />}
						actions={[
							{
								content: <FormattedMessage {...messages.flagClose} />,
								onClick: next,
							},
							{
								content: <FormattedMessage {...messages.flagReadCommunityPost} />,
								href: 'https://community.atlassian.com/t5/Jira-articles/We-re-testing-a-new-interface-for-Advanced-Roadmaps/ba-p/2328773',
								target: '_blank',
								onClick: openCommunityPost,
							},
						]}
					/>
				</>
			)}
		</>
	);
};
